/**
 * Program Incentive Dashboard
 * Displays program-wide incentive summary, milestone progress, and payout status
 * Integrates with payment infrastructure for financial tracking
 * Trust Marketplace - Configurable Milestone System
 */

import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { getWeaveinApiUrl } from '@/src/config';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { ProgramPaymentEnvelope } from '../benefits/ProgramPaymentEnvelope';
import { DeferralQueue } from '../benefits/DeferralQueue';
import {
  DollarSign,
  Users,
  Target,
  TrendingUp,
  Award,
  Clock,
  CheckCircle,
  AlertCircle,
  CreditCard,
  Calendar,
  Download
} from 'lucide-react';

interface ProgramIncentiveDashboardProps {
  programId: number;
}

interface IncentiveSummary {
  program_id: number;
  program_name: string;
  total_enrolled: number;
  active_members: number;

  milestones: {
    total_milestones: number;
    required_milestones: number;
    completed_milestones: number;
    in_progress_milestones: number;
  };

  payouts: {
    total_amount: number;
    cbo_amount: number;
    member_amount: number;
    pending_amount: number;
    approved_amount: number;
    paid_amount: number;
    pending_count: number;
  };

  budget: {
    total_budget: number;
    allocated_amount: number;
    disbursed_amount: number;
    remaining_budget: number;
  };

  milestones_details?: Array<{
    milestone_id: number;
    milestone_name: string;
    milestone_type: string;
    total_completions: number;
    completion_rate: number;
    avg_completion_days: number;
    total_payout_amount: number;
  }>;
}

const ProgramIncentiveDashboard: React.FC<ProgramIncentiveDashboardProps> = ({ programId }) => {
  const { t } = useAppTranslation();
  const [summary, setSummary] = useState<IncentiveSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchIncentiveSummary();
  }, [programId]);

  const fetchIncentiveSummary = async () => {
    try {
      setLoading(true);
      const url = getWeaveinApiUrl(`/api/programs/${programId}/incentives/summary`);
      const token = localStorage.getItem('auth_token');

      console.log('Fetching program incentive summary:', url);

      const cboId = localStorage.getItem('active_cbo_id');
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          ...(cboId && { 'X-CBO-ID': cboId })
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch incentive summary: ${response.status}`);
      }

      const data = await response.json();
      console.log('Incentive summary data:', data);
      setSummary(data);
    } catch (err) {
      console.error('Error fetching incentive summary:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatPercentage = (value: number) => {
    return `${Math.round(value)}%`;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="p-6 bg-red-50 border-red-200">
        <div className="flex items-center gap-2 text-red-800">
          <AlertCircle className="w-5 h-5" />
          <span className="font-medium">Error: {error}</span>
        </div>
      </Card>
    );
  }

  if (!summary) {
    return null;
  }

  const budgetUtilization = summary.budget.total_budget > 0
    ? (summary.budget.disbursed_amount / summary.budget.total_budget) * 100
    : 0;

  const milestoneCompletionRate = summary.milestones.total_milestones > 0
    ? (summary.milestones.completed_milestones / summary.milestones.total_milestones) * 100
    : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{summary.program_name}</h2>
          <p className="text-sm text-gray-600 mt-1">{t('incentives.program_incentive_dashboard', 'Program Incentive Dashboard')}</p>
        </div>
        <Button variant="outline">
          <Download className="w-4 h-4 mr-2" />
          {t('incentives.export_report', 'Export Report')}
        </Button>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Payouts */}
        <Card className="p-6 bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-green-600">{t('incentives.total_payouts', 'Total Payouts')}</p>
              <p className="text-3xl font-bold text-green-900 mt-2">
                {formatCurrency(summary.payouts.total_amount)}
              </p>
              <div className="flex items-center gap-2 mt-2 text-xs text-green-700">
                <span>CBO: {formatCurrency(summary.payouts.cbo_amount)}</span>
                <span>•</span>
                <span>Member: {formatCurrency(summary.payouts.member_amount)}</span>
              </div>
            </div>
            <div className="bg-green-500 rounded-full p-3">
              <DollarSign className="w-6 h-6 text-white" />
            </div>
          </div>
        </Card>

        {/* Pending Approvals */}
        <Card className="p-6 bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-yellow-600">{t('incentives.pending_approval', 'Pending Approval')}</p>
              <p className="text-3xl font-bold text-yellow-900 mt-2">
                {formatCurrency(summary.payouts.pending_amount)}
              </p>
              <p className="text-xs text-yellow-700 mt-2">
                {summary.payouts.pending_count} payouts awaiting approval
              </p>
            </div>
            <div className="bg-yellow-500 rounded-full p-3">
              <Clock className="w-6 h-6 text-white" />
            </div>
          </div>
        </Card>

        {/* Active Members */}
        <Card className="p-6 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-blue-600">{t('incentives.active_members', 'Active Members')}</p>
              <p className="text-3xl font-bold text-blue-900 mt-2">
                {summary.active_members}
              </p>
              <p className="text-xs text-blue-700 mt-2">
                of {summary.total_enrolled} enrolled
              </p>
            </div>
            <div className="bg-blue-500 rounded-full p-3">
              <Users className="w-6 h-6 text-white" />
            </div>
          </div>
        </Card>

        {/* Milestone Progress */}
        <Card className="p-6 bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-purple-600">{t('incentives.milestone_progress', 'Milestone Progress')}</p>
              <p className="text-3xl font-bold text-purple-900 mt-2">
                {formatPercentage(milestoneCompletionRate)}
              </p>
              <p className="text-xs text-purple-700 mt-2">
                {summary.milestones.completed_milestones} of {summary.milestones.total_milestones} completed
              </p>
            </div>
            <div className="bg-purple-500 rounded-full p-3">
              <Target className="w-6 h-6 text-white" />
            </div>
          </div>
        </Card>
      </div>

      {/* Budget Utilization */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{t('incentives.budget_utilization', 'Budget Utilization')}</h3>
            <p className="text-sm text-gray-600">{t('incentives.track_program_spending_and_remaining', 'Track program spending and remaining budget')}</p>
          </div>
          <Badge className={budgetUtilization > 80 ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}>
            {formatPercentage(budgetUtilization)} Used
          </Badge>
        </div>

        <div className="space-y-4">
          {/* Progress Bar */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">{t('incentives.budget_progress', 'Budget Progress')}</span>
              <span className="text-sm font-semibold text-gray-900">
                {formatCurrency(summary.budget.disbursed_amount)} / {formatCurrency(summary.budget.total_budget)}
              </span>
            </div>
            <div className="relative">
              <Progress value={budgetUtilization} className="h-3" />
              <div
                className={`absolute top-0 left-0 h-3 rounded-full transition-all ${
                  budgetUtilization > 80 ? 'bg-red-500' : 'bg-green-500'
                }`}
                style={{ width: `${budgetUtilization}%` }}
              />
            </div>
          </div>

          {/* Budget Breakdown */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t">
            <div>
              <p className="text-xs text-gray-500 mb-1">{t('incentives.allocated', 'Allocated')}</p>
              <p className="text-lg font-semibold text-gray-900">
                {formatCurrency(summary.budget.allocated_amount)}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">{t('incentives.disbursed', 'Disbursed')}</p>
              <p className="text-lg font-semibold text-green-600">
                {formatCurrency(summary.budget.disbursed_amount)}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">{t('incentives.remaining', 'Remaining')}</p>
              <p className="text-lg font-semibold text-blue-600">
                {formatCurrency(summary.budget.remaining_budget)}
              </p>
            </div>
          </div>
        </div>
      </Card>

      {/* Payout Status Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="bg-yellow-100 rounded-full p-2">
              <Clock className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">{t('incentives.pending', 'Pending')}</h4>
              <p className="text-xs text-gray-500">{t('incentives.awaiting_approval', 'Awaiting approval')}</p>
            </div>
          </div>
          <p className="text-2xl font-bold text-yellow-600">
            {formatCurrency(summary.payouts.pending_amount)}
          </p>
          <p className="text-xs text-gray-500 mt-1">{summary.payouts.pending_count} payouts</p>
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="bg-blue-100 rounded-full p-2">
              <CheckCircle className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">{t('incentives.approved', 'Approved')}</h4>
              <p className="text-xs text-gray-500">{t('incentives.ready_for_processing', 'Ready for processing')}</p>
            </div>
          </div>
          <p className="text-2xl font-bold text-blue-600">
            {formatCurrency(summary.payouts.approved_amount)}
          </p>
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="bg-green-100 rounded-full p-2">
              <CreditCard className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">{t('incentives.paid', 'Paid')}</h4>
              <p className="text-xs text-gray-500">{t('incentives.completed_payouts', 'Completed payouts')}</p>
            </div>
          </div>
          <p className="text-2xl font-bold text-green-600">
            {formatCurrency(summary.payouts.paid_amount)}
          </p>
        </Card>
      </div>

      {/* Milestone Performance Details */}
      {summary.milestones_details && summary.milestones_details.length > 0 && (
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('incentives.milestone_performance', 'Milestone Performance')}</h3>

          <div className="space-y-3">
            {summary.milestones_details.map((milestone) => (
              <div
                key={milestone.milestone_id}
                className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="font-semibold text-gray-900">{milestone.milestone_name}</h4>
                      <Badge variant="outline" className="text-xs">
                        {milestone.milestone_type}
                      </Badge>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500">{t('incentives.completions', 'Completions')}</p>
                        <p className="font-semibold text-gray-900">{milestone.total_completions}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">{t('incentives.completion_rate', 'Completion Rate')}</p>
                        <p className="font-semibold text-gray-900">
                          {formatPercentage(milestone.completion_rate)}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500">{t('incentives.avg_days', 'Avg. Days')}</p>
                        <p className="font-semibold text-gray-900">
                          {Math.round(milestone.avg_completion_days)}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500">{t('incentives.total_paid', 'Total Paid')}</p>
                        <p className="font-semibold text-green-600">
                          {formatCurrency(milestone.total_payout_amount)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Action Items */}
      {summary.payouts.pending_count > 0 && (
        <Card className="p-6 bg-gradient-to-r from-orange-50 to-red-50 border-orange-200">
          <div className="flex items-start gap-4">
            <div className="bg-orange-500 rounded-full p-3">
              <AlertCircle className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h4 className="text-lg font-semibold text-gray-900 mb-1">
                Action Required: {summary.payouts.pending_count} Pending Approvals
              </h4>
              <p className="text-sm text-gray-700 mb-3">
                {formatCurrency(summary.payouts.pending_amount)} in incentive payouts are awaiting your approval.
              </p>
              <Button className="bg-orange-600 hover:bg-orange-700">
                <CheckCircle className="w-4 h-4 mr-2" />
                {t('incentives.review_pending_payouts', 'Review Pending Payouts')}
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* The benefit ceiling that governs these payouts, and anything it is
          currently holding. It sits with the incentive numbers because this is
          where somebody looks when a payment did not land when they expected. */}
      <ProgramPaymentEnvelope programId={programId} />
      <DeferralQueue programId={programId} />
    </div>
  );
};

export default ProgramIncentiveDashboard;
